import React, { useState, useCallback, useMemo, memo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import { columnNameError } from '../../../utils/tableCreationNames';
import { getPeriodStructureLabel, DEFAULT_PERIOD } from '../../../utils/periodUtils';
import { normalizeMetricUpdateBehaviors } from '../../../utils/metricUpdateBehaviors';

const dataTypes = ['Text', 'Number', 'Date', 'Yes/No'];
const updateBehaviors = ['Add', 'Overwrite'];

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const NestedBox = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(4),
  paddingLeft: theme.spacing(2),
  borderLeft: `2px solid ${theme.palette.divider}`,
}));

const EditableField = memo(({ initialValue, onSave, label, fullWidth = true, size = "small" }) => {
  const [editingValue, setEditingValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);

  const handleChange = (e) => {
    setEditingValue(e.target.value);
  };

  const handleBlur = () => {
    if (editingValue !== initialValue) {
      onSave(editingValue.trim());
    }
    setIsEditing(false);
  };

  const handleFocus = () => {
    setEditingValue(initialValue);
    setIsEditing(true);
  };

  return (
    <TextField
      fullWidth={fullWidth}
      label={label}
      value={isEditing ? editingValue : initialValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      size={size}
    />
  );
});

// One stable empty schema, so the edit callbacks below keep their identity
// while a null value is being edited as empty.
const EMPTY_SCHEMA = Object.freeze({});

function unusedName(schema, prefix) {
  const names = new Set(Object.keys(schema).map(name => name.trim().toLowerCase()));
  let suffix = 1;
  while (names.has(`${prefix}${suffix}`.toLowerCase())) suffix += 1;
  return `${prefix}${suffix}`;
}

// The metric columns of a time-series table, the only type that has them. Which
// metrics are mandatory, per file and per unit, is the creation form's own step.
function ValueColumnBuilder({ value, periodType = DEFAULT_PERIOD, onChange }) {
  const [nameError, setNameError] = useState('');
  // Metrics are always on (M196): the switch that emitted a null schema is
  // gone, and a value still holding null (a draft saved before) edits as an
  // empty schema the Metrics step refuses to leave empty.
  const sourceSchema = value?.schema || EMPTY_SCHEMA;
  const schema = useMemo(() => normalizeMetricUpdateBehaviors(sourceSchema), [sourceSchema]);

  const updateSchema = useCallback((newSchema) => {
    setNameError('');
    onChange({
      schema: normalizeMetricUpdateBehaviors(newSchema)
    });
  }, [onChange]);

  const addValue = useCallback(() => {
    const newSchema = { ...schema };
    const newKey = unusedName(newSchema, 'value');
    newSchema[newKey] = {
      type: 'Number',
      updateBehavior: 'Overwrite'
    };
    updateSchema(newSchema);
  }, [schema, updateSchema]);

  const addGroup = useCallback(() => {
    const newSchema = { ...schema };
    const newKey = unusedName(newSchema, 'group');
    newSchema[newKey] = {};
    updateSchema(newSchema);
  }, [schema, updateSchema]);

  const addNestedValue = useCallback((groupKey) => {
    const newSchema = { ...schema };
    const newKey = unusedName(newSchema[groupKey], 'value');
    newSchema[groupKey] = {
      ...newSchema[groupKey],
      [newKey]: {
        type: 'Number',
        updateBehavior: 'Overwrite'
      }
    };
    updateSchema(newSchema);
  }, [schema, updateSchema]);

  const deleteItem = useCallback((key) => {
    const newSchema = { ...schema };
    delete newSchema[key];
    updateSchema(newSchema);
  }, [schema, updateSchema]);

  const deleteNestedItem = useCallback((groupKey, valueKey) => {
    const newSchema = { ...schema };
    const newGroup = { ...newSchema[groupKey] };
    delete newGroup[valueKey];
    newSchema[groupKey] = newGroup;
    updateSchema(newSchema);
  }, [schema, updateSchema]);

  const updateItemName = useCallback((oldKey, newName) => {
    const error = columnNameError(newName);
    if (error) {
      setNameError(error);
      return;
    }
    const collision = Object.keys(schema).find(name => (
      name !== oldKey && name.trim().toLowerCase() === newName.toLowerCase()
    ));
    if (collision) {
      setNameError(`A metric or group named "${collision}" already exists.`);
      return;
    }
    if (newName === oldKey) {
      setNameError('');
      return;
    }
    const newSchema = { ...schema };
    const itemValue = newSchema[oldKey];
    delete newSchema[oldKey];
    newSchema[newName] = itemValue;
    updateSchema(newSchema);
  }, [schema, updateSchema]);

  const updateNestedItemName = useCallback((groupKey, oldKey, newName) => {
    const error = columnNameError(newName);
    if (error) {
      setNameError(error);
      return;
    }
    const collision = Object.keys(schema[groupKey]).find(name => (
      name !== oldKey && name.trim().toLowerCase() === newName.toLowerCase()
    ));
    if (collision) {
      setNameError(`A metric named "${collision}" already exists in "${groupKey}".`);
      return;
    }
    if (newName === oldKey) {
      setNameError('');
      return;
    }
    const newSchema = { ...schema };
    const newGroup = { ...newSchema[groupKey] };
    const itemValue = newGroup[oldKey];
    delete newGroup[oldKey];
    newGroup[newName] = itemValue;
    newSchema[groupKey] = newGroup;
    updateSchema(newSchema);
  }, [schema, updateSchema]);

  const updateItemProperty = useCallback((key, property, newValue) => {
    const newSchema = { ...schema };
    newSchema[key] = {
      ...newSchema[key],
      [property]: newValue
    };
    updateSchema(newSchema);
  }, [schema, updateSchema]);

  const updateNestedItemProperty = useCallback((groupKey, valueKey, property, newValue) => {
    const newSchema = { ...schema };
    newSchema[groupKey] = {
      ...newSchema[groupKey],
      [valueKey]: {
        ...newSchema[groupKey][valueKey],
        [property]: newValue
      }
    };
    updateSchema(newSchema);
  }, [schema, updateSchema]);

  return (
    <Box sx={{ p: 2 }}>
      {nameError && <Alert severity="error" onClose={() => setNameError('')}>{nameError}</Alert>}
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {getPeriodStructureLabel(periodType)}
      </Typography>

      {Object.entries(schema).map(([key, schemaValue]) => (
        <StyledPaper key={key}>
          {typeof schemaValue === 'object' && !schemaValue.type ? (
            // Group with nested values
            <div>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={11}>
                  <EditableField
                    initialValue={key}
                    onSave={(newName) => updateItemName(key, newName)}
                    label="Group Name"
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton onClick={() => deleteItem(key)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>

              <NestedBox>
                {Object.entries(schemaValue).map(([nestedKey, nestedValue]) => (
                  <Box key={`${key}-${nestedKey}`} sx={{ mt: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3}>
                        <EditableField
                          initialValue={nestedKey}
                          onSave={(newName) => updateNestedItemName(key, nestedKey, newName)}
                          label="Value Name"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={nestedValue.type}
                            onChange={(e) => updateNestedItemProperty(key, nestedKey, 'type', e.target.value)}
                            label="Type"
                          >
                            {dataTypes.map(type => (
                              <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Update Behavior</InputLabel>
                          <Select
                            value={nestedValue.updateBehavior}
                            disabled={nestedValue.type !== 'Number'}
                            onChange={(e) => updateNestedItemProperty(key, nestedKey, 'updateBehavior', e.target.value)}
                            label="Update Behavior"
                          >
                            {updateBehaviors.map(behavior => (
                              <MenuItem key={behavior} value={behavior}>{behavior}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton onClick={() => deleteNestedItem(key, nestedKey)} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => addNestedValue(key)}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2 }}
                >
                  Add Value
                </Button>
              </NestedBox>
            </div>
          ) : (
            // Single value
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={3}>
                <EditableField
                  initialValue={key}
                  onSave={(newName) => updateItemName(key, newName)}
                  label="Value Name"
                />
              </Grid>
              <Grid item xs={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={schemaValue.type}
                    onChange={(e) => updateItemProperty(key, 'type', e.target.value)}
                    label="Type"
                  >
                    {dataTypes.map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Update Behavior</InputLabel>
                  <Select
                    value={schemaValue.updateBehavior}
                    disabled={schemaValue.type !== 'Number'}
                    onChange={(e) => updateItemProperty(key, 'updateBehavior', e.target.value)}
                    label="Update Behavior"
                  >
                    {updateBehaviors.map(behavior => (
                      <MenuItem key={behavior} value={behavior}>{behavior}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={1}>
                <IconButton onClick={() => deleteItem(key)} size="small" color="error">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          )}
        </StyledPaper>
      ))}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          startIcon={<AddIcon />}
          onClick={addValue}
          variant="outlined"
          size="small"
        >
          Add Single Value
        </Button>
        <Button
          startIcon={<AddIcon />}
          onClick={addGroup}
          variant="outlined"
          size="small"
        >
          Add Group
        </Button>
      </Box>
    </Box>
  );
}

export default memo(ValueColumnBuilder);
